import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { unitStatus } from '@/lib/storageList'
import type { ApiStorageNode, StorageCondition } from '@/types/storage'
import { KeptAtBadge } from '../KeptAtBadge'
import { LotLocator } from '../LotLocator'
import { UnitRoomControl } from './UnitRoomControl'

const STATUS_DOT = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-rose-500',
} as const

/**
 * The unit's name, what it is kept at, one summary line, and the three things
 * people come here to do. Every button is optional: the page passes only the
 * ones this user's permissions and features allow, so nothing renders that
 * would 403.
 */
export function UnitHeader({
  unit,
  condition,
  summary,
  status,
  onLocated,
  onSettings,
  settingsOpen,
  onPutStock,
}: {
  unit: ApiStorageNode
  condition: StorageCondition | null
  /** "40 of 40 spaces used", "2 blocked", "Zone Cell lab". */
  summary: string[]
  status: ReturnType<typeof unitStatus> | null
  /** Find an item. Omitted without the inventory parts. */
  onLocated?: (nodeId: string, path: string) => void
  /** Open the UNIT's Settings, whatever is selected. Omitted when there is nothing in them this user may do. */
  onSettings?: () => void
  settingsOpen?: boolean
  /** Put stock here. Omitted without `inventory.manage` or the inventory parts. */
  onPutStock?: () => void
}) {
  const [finding, setFinding] = useState(false)

  return (
    <div className='flex flex-wrap items-center justify-between gap-4'>
      <div className='min-w-0 space-y-1'>
        <h1 className='flex flex-wrap items-center gap-x-2.5 gap-y-1 text-2xl font-semibold tracking-tight text-foreground'>
          {unit.code}
          {unit.name && <span className='text-base font-normal text-muted-foreground'>{unit.name}</span>}
          <KeptAtBadge condition={condition} />
        </h1>
        {/* Where the unit stands (z8nrz7fw25): the outside of its address, above its own configuration. */}
        <UnitRoomControl equipmentId={unit.equipmentId} unitCode={unit.code} />
        <p className='flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground'>
          {summary.map((part, i) => (
            <span key={part} className='inline-flex items-center gap-1.5'>
              {i > 0 && <span aria-hidden>·</span>}
              {part}
            </span>
          ))}
          {status && (
            <span className='inline-flex items-center gap-1.5'>
              {summary.length > 0 && <span aria-hidden>·</span>}
              <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[status.tone])} aria-hidden />
              <span className='font-medium text-foreground'>{status.label}</span>
            </span>
          )}
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        {onLocated && (
          <Popover open={finding} onOpenChange={setFinding}>
            <PopoverTrigger asChild>
              <Button variant='outline'>⌕ Find an item</Button>
            </PopoverTrigger>
            <PopoverContent align='end' className='w-96'>
              <LotLocator
                onLocated={(nodeId, path) => {
                  setFinding(false)
                  onLocated(nodeId, path)
                }}
              />
            </PopoverContent>
          </Popover>
        )}
        {onSettings && (
          <Button
            variant='outline'
            aria-pressed={settingsOpen}
            className={cn(settingsOpen && 'bg-muted')}
            onClick={onSettings}
          >
            ⚙ Settings
          </Button>
        )}
        {onPutStock && <Button onClick={onPutStock}>＋ Put stock here</Button>}
      </div>
    </div>
  )
}
